/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material';
import DataGrid, { Column, Scrolling, Paging, Pager, HeaderFilter, Sorting } from 'devextreme-react/data-grid';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getCompanies, getSchemes, getPeriods } from '../../../store/slices/account-entries';
import MySnackbar from '../../../components/MySnackbar';
// @ts-ignore: side-effect CSS import without type declarations
import 'devextreme/dist/css/dx.light.css';
import { cancelMigrationProcess, getMigrationResume, registerMigrationProcess } from '../../../api/accountApi';

interface AccountingEntryProcess {
  Id: string;
  RUCCorporation: string;
  CompanyCode: string;
  CompanyName: string;
  SchemeCode: string;
  SchemeName: string;
  Year: number;
  AccountingEntryName: string;
  CreationUser: string;
  CreationDate: string;
  ModificationUser: string;
  ModificationDate: string;
  Status: number;
}

const getStatusText = (status: number): string => {
  switch (status) {
    case 0:
      return 'Pendiente';
    case -1:
      return 'En proceso';
    case 1:
      return 'Completado';
    case 2:
      return 'Error';
    case 3:
      return 'Cancelado';
    default:
      return 'Desconocido';
  }
};


export const SyncJournal: React.FC = () => {
  
  const { companyselected, connectionId, sessionData } = useSelector((state: RootState) => state.auth);
  const { companies, scheme, periods } = useSelector((state: RootState) => state.accountEntries);

  const [filterValues, setFilterValues] = useState({
    ruc: '',
    esquema: '',
    año: '',
  });
  const [data, setData] = useState<AccountingEntryProcess[]>([]);
  const [loading, setLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    if (companyselected?.Token && connectionId) {
      getCompanies();
    }
  }, [companyselected?.Token, connectionId]);

  useEffect(() => {
    if (filterValues.ruc && filterValues.ruc !== 'todos') {
      getSchemes(filterValues.ruc);
      getPeriods(filterValues.ruc);
    }
  }, [filterValues.ruc]);

  const rucOptions = useMemo(() => {
    const opts = companies.map(comp => ({ 
      label: `${comp.CodEmpresa} - ${comp.Descripcion}`, 
      value: comp.CodEmpresa,
      companyName: comp.Descripcion 
    }));
    return [{ label: 'Todos', value: 'todos', companyName: '' }, ...opts];
  }, [companies]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMigrationResume()
      setData(result);
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Error al cargar los datos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterValues, companyselected, connectionId]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      if (!importModalOpen) {
        loadData();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const handleImportConfirm = async () => {

    const selectedCompany = companies.find(c => c.CodEmpresa === filterValues.ruc);
    const selectedScheme = scheme.find(s => s.CodEsquemaContable === filterValues.esquema);

    if (!selectedCompany || !selectedScheme) {
      setSnackbar({ open: true, message: 'No se encontraron datos de la empresa o esquema', severity: 'error' });
      return;
    }

    try {
      const registerData = {
        CompanyCode: filterValues.ruc,
        CompanyName: selectedCompany.Descripcion,
        SchemeCode: filterValues.esquema,
        SchemeName: selectedScheme.Descripcion,
        Year: parseInt(filterValues.año),
        CreationUser: sessionData?.UserName || ''
      };
      
      await registerMigrationProcess(registerData);
      setSnackbar({ open: true, message: 'Solicitud de importación registrada exitosamente', severity: 'success' });
      setImportModalOpen(false);
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al registrar la solicitud de importación', severity: 'error' });
    }
  };

  const handleCancel = async (row: AccountingEntryProcess) => {
    if (row.Status === 1 || row.Status === 2 || row.Status === 3) {
      setSnackbar({ open: true, message: 'No se puede cancelar un proceso completado, con error o ya cancelado', severity: 'error' });
      return;
    }

    setCancellingId(row.Id);
    try {
      const cancelData = {
        Id: row.Id,
        ModificationUser: sessionData?.UserName || ''
      };
      await cancelMigrationProcess(cancelData);
      setSnackbar({ open: true, message: 'Proceso cancelado exitosamente', severity: 'success' });
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al cancelar el proceso', severity: 'error' });
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <Box sx={{ p: 0, height: '100%', display: 'flex', position: 'relative', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5">Sincronizar Libro Diario</Typography>
          <Button variant="contained" onClick={() => setImportModalOpen(true)}>
            Importar
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            dataSource={data}
            showBorders
            width="100%"
            height="90%"
            columnAutoWidth
            rowAlternationEnabled={false}
          >
            <Scrolling mode="standard" />
            <Paging defaultPageSize={10} />
            <Pager showPageSizeSelector allowedPageSizes={[10, 25, 50]} showInfo />
            <Sorting mode="multiple" />
            <HeaderFilter visible={true} allowSearch={true} />
            
            <Column dataField="CompanyCode" caption="RUC" />
            <Column dataField="CompanyName" caption="Empresa" />
            <Column dataField="SchemeCode" caption="Esquema" />
            <Column dataField="SchemeName" caption="Nombre Esquema" />
            <Column dataField="Year" caption="Año" dataType="number" />
            <Column dataField="AccountingEntryName" caption="Nombre Asiento" />
            <Column 
              dataField="Status" 
              caption="Estado"
              cellRender={(cell: any) => (
                <Typography sx={{ fontWeight: 'bold' }}>
                  {getStatusText(cell.value)}
                </Typography>
              )}
            />
            <Column dataField="CreationUser" caption="Creado por" />
            <Column 
              dataField="CreationDate" 
              caption="Fecha creación" 
              dataType="date" 
              format="dd/MM/yyyy HH:mm" 
            />
            <Column dataField="ModificationUser" caption="Modificado por" />
            <Column 
              dataField="ModificationDate" 
              caption="Fecha modificación" 
              dataType="date" 
              format="dd/MM/yyyy HH:mm" 
            />
            <Column
              caption="Acciones"
              alignment="center"
              width={120}
              cellRender={(cell: any) => {
                const row = cell.data as AccountingEntryProcess;
                const canCancel = row.Status === 0 || row.Status === -1;
                return (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => handleCancel(row)}
                    disabled={!canCancel || cancellingId === row.Id}
                  >
                    {cancellingId === row.Id ? <CircularProgress size={20} /> : 'Cancelar'}
                  </Button>
                );
              }}
            />
          </DataGrid>
        )}
      </Box>

      <Dialog open={importModalOpen} onClose={() => setImportModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar libro diario</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>RUC</InputLabel>
              <Select
                value={filterValues.ruc}
                onChange={(e) => {
                  const selectedRuc = e.target.value;
                  const selectedCompany = companies.find(c => c.CodEmpresa === selectedRuc);
                  setFilterValues(prev => ({ 
                    ...prev, 
                    ruc: selectedRuc,
                    esquema: '',
                    año: '',
                    companyName: selectedCompany?.Descripcion || '',
                    schemeName: ''
                  }));
                }}
                label="RUC"
              >
                {rucOptions.filter(opt => opt.value !== 'todos').map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!filterValues.ruc}>
              <InputLabel>Esquema</InputLabel>
              <Select
                value={filterValues.esquema}
                onChange={(e) => {
                  const selectedScheme = e.target.value;
                  const selectedSchemeObj = scheme.find(s => s.CodEsquemaContable === selectedScheme);
                  setFilterValues(prev => ({ 
                    ...prev, 
                    esquema: selectedScheme,
                    schemeName: selectedSchemeObj?.Descripcion || ''
                  }));
                }}
                label="Esquema"
              >
                {scheme.map(schemeItem => (
                  <MenuItem key={schemeItem.CodEsquemaContable} value={schemeItem.CodEsquemaContable}>
                    {schemeItem.CodEsquemaContable} - {schemeItem.Descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!filterValues.ruc}>
              <InputLabel>Año</InputLabel>
              <Select
                value={filterValues.año}
                onChange={(e) => setFilterValues(prev => ({ ...prev, año: e.target.value }))}
                label="Año"
              >
                {periods
                  .map(p => p.CodPeriodoContable.substring(0, 4))
                  .filter((value, index, self) => self.indexOf(value) === index)
                  .map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            disabled={!filterValues.ruc || !filterValues.esquema || !filterValues.año}
          >
            Importar
          </Button>
        </DialogActions>
      </Dialog>

      <MySnackbar
        open={snackbar.open}
        msg={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};